import { Select, Tag, Typography, theme } from "antd";
import type { RefSelectProps } from "antd/es/select";
import { ReactNode, useEffect, useRef, useState } from "react";
import { Subscription, subscription } from "../utils/http";

// Structural item/group shapes — this is a generic control, so it declares what it reads instead of
// importing a feature module's types. The calendar module's ParticipantItem/TargetGroup (and any
// other grouped-target loader) satisfy these structurally.
export interface GroupedTargetItem {
  readonly display: string;
  readonly subDisplay?: string;
  readonly value?: any;
  readonly dataItem?: {
    // Drives the option's type tag: "company" / "department" / anything else = a user.
    readonly __type?: string;
    // For user targets: a plain company member vs an assigned affiliate.
    readonly __kind?: string;
    // For user targets: the user's role name — when present it labels the tag instead of "Member".
    readonly __roleName?: string;
    readonly [key: string]: any;
  };
}

// One group of pickable targets (e.g. "Companies" / "Departments" / "Users").
export interface GroupedTargetGroup<
  TItem extends GroupedTargetItem = GroupedTargetItem,
> {
  readonly display: string;
  readonly value: TItem[];
}

export interface GroupedTargetPickerProps<
  TItem extends GroupedTargetItem = GroupedTargetItem,
> {
  /** Loads the grouped options for a search term — the caller owns the scoping (company-scoped,
   *  platform-wide, …); the control only renders/searches/picks. */
  readonly loadGroups: (search: string) => Promise<GroupedTargetGroup<TItem>[]>;
  /** Receives each picked target; the control clears itself after a pick (it adds, it never holds a value). */
  readonly onPick: (item: TItem) => void;
  /** A scope key — when it changes, loaded options are dropped (they belong to the previous scope). */
  readonly resetKey?: unknown;
  /** Optional label above the control, styled like a DynamicForm field label. */
  readonly label?: string;
  readonly placeholder?: string;
  readonly disabled?: boolean;
  readonly className?: string;
  /** Optional row pinned above the options inside the dropdown (e.g. an "Add all" toggle). */
  readonly dropdownHeader?: ReactNode;
  /** Toggle mode (set together with `onUnpick`): the values already picked. Their options show a check,
   *  clicking one again calls `onUnpick`, and the dropdown stays open between clicks. */
  readonly selectedValues?: string[];
  readonly onUnpick?: (item: TItem) => void;
}

interface TargetOption<TItem> {
  readonly label: string;
  readonly value: string;
  readonly item: TItem;
}

interface OptionGroup<TItem> {
  readonly label: ReactNode;
  readonly title: string;
  readonly options: TargetOption<TItem>[];
}

// The small type tag rendered on each option. Colors follow the platform's tag semantics: geekblue for
// company-level entities, blue for departments (the Attendee blue family), gold for affiliates (the
// "external person" accent), and an uncolored tag for plain members — subtle, not a rainbow.
function typeTag(item: GroupedTargetItem): { label: string; color?: string } {
  switch (item.dataItem?.__type) {
    case "company":
      return { label: "Company", color: "geekblue" };
    case "department":
      return { label: "Department", color: "blue" };
    default:
      return item.dataItem?.__kind === "affiliate"
        ? { label: "Affiliate", color: "gold" }
        : { label: item.dataItem?.__roleName || "Member" };
  }
}

/**
 * A server-searched select offering grouped targets (companies / departments / users), each option
 * carrying a type tag and its scope as muted secondary text. Picking fires `onPick` and clears the
 * control — staging vs immediate action stays the caller's concern. The grouped-target picker behind
 * ParticipantTargetSelect (calendar/trainings) and the assignment dialogs.
 */
function GroupedTargetPicker<TItem extends GroupedTargetItem = GroupedTargetItem>(
  props: GroupedTargetPickerProps<TItem>,
) {
  const { token } = theme.useToken();
  const [groups, setGroups] = useState<OptionGroup<TItem>[]>([]);
  const [loading, setLoading] = useState(false);
  // The in-flight (possibly delay-pending) search — superseded searches are cancelled.
  const pending = useRef<Subscription<GroupedTargetGroup<TItem>[]>>();
  const selectRef = useRef<RefSelectProps>(null);

  const search = (text: string, delay: number) => {
    pending.current?.unsubscribe();
    setLoading(true);
    const request = subscription(() => props.loadGroups(text), delay);
    pending.current = request;
    request
      .then((items) => {
        if (pending.current !== request) return;
        setGroups(
          items
            .filter((group) => group.value.length)
            .map((group) => ({
              label: (
                <Typography.Text type="secondary" className="text-xs font-medium uppercase">
                  {group.display}
                </Typography.Text>
              ),
              title: group.display,
              options: group.value.map((item) => ({
                label: item.display,
                value: item.value as string,
                item,
              })),
            })),
        );
        setLoading(false);
      })
      .catch(() => {
        if (pending.current !== request) return;
        setGroups([]);
        setLoading(false);
      });
  };

  const toggle = !!props.onUnpick && !!props.selectedValues;
  const findItem = (value: string) =>
    groups.flatMap((group) => group.options).find((option) => option.value === value)?.item;

  // A scope switch invalidates the loaded options (they belong to the previous scope).
  useEffect(() => {
    setGroups([]);
  }, [props.resetKey]);

  return (
    // `min-w-0` matters: as a flex child the control must be allowed to shrink below its content
    // width, or a long option/selected label stretches it past its container (e.g. a modal edge).
    <div className={`flex w-full min-w-0 flex-col p-1 ${props.className ?? ""}`}>
      {props.label && (
        <Typography.Text className="text-sm" style={{ color: token.colorTextDescription }}>
          {props.label}
        </Typography.Text>
      )}
      {/* Value is a string[] in toggle mode, null otherwise. */}
      <Select<string[] | string | null, OptionGroup<TItem>>
        ref={selectRef}
        className="w-full"
        // The dropdown tracks the control's width — long labels ellipsize inside options instead
        // of the dropdown (and control) growing to fit them.
        popupMatchSelectWidth
        disabled={props.disabled}
        placeholder={props.placeholder ?? "Search targets"}
        {...(toggle
          ? {
              // Multiple mode gives the per-option check + click-to-deselect and keeps the dropdown open;
              // the chips are hidden — the box just counts what's picked.
              mode: "multiple" as const,
              value: props.selectedValues,
              maxTagCount: 0,
              maxTagPlaceholder: (omitted: unknown[]) => `${omitted.length} added`,
              autoClearSearchValue: false,
              onSelect: (value: string | null) => {
                const picked = value != null ? findItem(value) : undefined;
                if (picked) props.onPick(picked);
              },
              onDeselect: (value: string | null) => {
                const item = value != null ? findItem(value) : undefined;
                if (item) props.onUnpick!(item);
              },
            }
          : {
              // Always empty: an "add" control — each pick is handed to the caller, never held.
              value: null,
              onChange: (value: string[] | string | null) => {
                const picked = typeof value === "string" ? findItem(value) : undefined;
                if (picked) props.onPick(picked);
              },
            })}
        options={groups}
        loading={loading}
        showSearch
        filterOption={false}
        defaultActiveFirstOption={false}
        notFoundContent={
          <Typography.Text type="secondary">
            {loading ? "Searching…" : "No matches"}
          </Typography.Text>
        }
        optionRender={(option) => {
          const item = (option.data as TargetOption<TItem>).item;
          const tag = typeTag(item);
          return (
            <div className="flex min-w-0 flex-col">
              <div className="flex min-w-0 flex-row items-center gap-2">
                <Typography.Text ellipsis className="min-w-0">
                  {item.display}
                </Typography.Text>
                <Tag bordered={false} color={tag.color} className="m-0 shrink-0">
                  {tag.label}
                </Tag>
              </div>
              {item.subDisplay && (
                <Typography.Text ellipsis type="secondary" className="min-w-0 text-xs">
                  {item.subDisplay}
                </Typography.Text>
              )}
            </div>
          );
        }}
        dropdownRender={(menu) =>
          props.dropdownHeader ? (
            <>
              <div
                className="mb-1 border-0 border-b border-solid px-3 pb-2 pt-1"
                style={{ borderColor: token.colorBorderSecondary }}
                // Focus stays in the search box while clicking; then the header action closes the
                // dropdown (blur), e.g. after "Add All".
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => selectRef.current?.blur()}
              >
                {props.dropdownHeader}
              </div>
              {menu}
            </>
          ) : (
            menu
          )
        }
        onSearch={(text) => search(text, 250)}
        onDropdownVisibleChange={(open) => {
          if (open) search("", 0);
        }}
      />
    </div>
  );
}

export default GroupedTargetPicker;
